import * as React from 'react'

import moment from 'moment'
import type { Moment } from 'moment'

import { DayActivityModel } from 'app/models/dayActivityModel'
import { formatTimespan } from 'app/common/dateHelper'

import { useScene } from '../context/SceneContext'
import { useConfig } from '../context/ConfigContext'
import { BottomTimeFlag } from './BottomTimeFlag'

export type DayActivityBlockProps = {
  dayActivity: DayActivityModel
  index: number
}

const shouldRenderInfo = (time: Moment, startDate: Moment): boolean => {
  const currentTime = moment(time)
  const endDate = startDate.clone().add(1, 'days')
  return currentTime >= startDate && currentTime <= endDate
}

const drawStartDayInfo = (dayActivity: DayActivityModel) => {
  return (
    <div className="tw-text-medium tw-grid tw-w-fit tw-place-items-center tw-rounded tw-bg-[#1F2937]/95 tw-px-1 tw-text-sm tw-text-white">
      {moment(dayActivity.startTime).format('HH:mm')}
    </div>
  )
}

const drawEndDayInfo = (
  dayActivity: DayActivityModel,
  showDrivingExtension: boolean,
) => {
  const endTime = moment(dayActivity.endTime)
  const extensionNo = (dayActivity as any).dailyDrivingTimeExtensionNo

  return (
    <div className="tw-flex tw-w-fit tw-items-center tw-gap-1 tw-rounded tw-bg-[#1F2937]/90 tw-px-1 tw-text-sm tw-text-white">
      <p className="tw-font-medium tw-text-[#E4E9F7]">
        {endTime.format('HH:mm')}
      </p>
      <div>&Sigma;</div>
      <p className="tw-font-light">{formatTimespan(dayActivity.duration)}</p>
      <div className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-[#E03A3E]"></div>
      <div className="tw-flex tw-items-center tw-text-nowrap tw-font-light">
        {formatTimespan(dayActivity.drivingTime)}
        {showDrivingExtension && extensionNo > 0 ? ` (${extensionNo}x10)` : ''}
      </div>
    </div>
  )
}

export const DayActivityBlock: React.FC<DayActivityBlockProps> = React.memo(
  (props: DayActivityBlockProps) => {
    const scene = useScene()
    const { printMode } = useConfig()
    const { dayActivity } = props
    const startTime = scene.startTime

    return (
      <div>
        {shouldRenderInfo(dayActivity.startTime, startTime) && (
          <BottomTimeFlag
            left={scene.timeToX(dayActivity.startTime)}
            chartWidth={scene.width}
            inner="right"
          >
            {drawStartDayInfo(dayActivity)}
          </BottomTimeFlag>
        )}
        {shouldRenderInfo(dayActivity.endTime, startTime) && (
          <BottomTimeFlag
            left={scene.timeToX(dayActivity.endTime)}
            chartWidth={scene.width}
            inner="left"
          >
            {drawEndDayInfo(dayActivity, printMode)}
          </BottomTimeFlag>
        )}
      </div>
    )
  },
)

DayActivityBlock.displayName = 'DayActivityBlock'
